import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';

const logoUrl = "https://storage.helloreaddy.io/project_files/8cf86b48-d09c-4ec8-816e-12bbed2b078b/1e3fa9b9-1413-4092-84fc-01303456e9e9_compressed_Biff-Digital-Logo.webp";

const hizmetlerLinks = [
  { label: 'Performans Pazarlama', href: '/hizmetler/performans-pazarlama' },
  { label: 'SEO & GEO', href: '/hizmetler/seo' },
  { label: 'Marka Stratejisi & Branding', href: '/hizmetler/marka-stratejisi' },
  { label: 'Web Geliştirme & Yazılım', href: '/hizmetler/web-gelistirme' },
  { label: 'Nöropazarlama & Davranışsal Analiz', href: '/hizmetler/noropazarlama' },
  { label: 'Kampanya & İtibar Yönetimi', href: '/hizmetler/kampanya-yonetimi' },
];

const sektorlerLinks = [
  { label: 'E-Ticaret', href: '/sektorler/e-ticaret-dijital-pazarlama' },
  { label: 'Endüstriyel & B2B', href: '/sektorler/endustriyel-b2b-pazarlama' },
  { label: 'İhracat', href: '/sektorler/ihracat' },
  { label: 'Siyasi Kampanya', href: '/sektorler/siyasi-kampanya' },
];

const anaLinks = [
  { label: 'Vakalar', href: '/basarili-dijital-projeler' },
  { label: 'İçgörüler', href: '/dijital-pazarlama-blog' },
  { label: 'Hakkımızda', href: '/ankara-dijital-pazarlama-ajansi' },
  { label: 'İletişim', href: '/iletisim' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [sectorsOpen, setSectorsOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [mobileSectorsOpen, setMobileSectorsOpen] = useState(false);
  const servicesRef = useRef<HTMLDivElement>(null);
  const sectorsRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const isHome = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 80);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (servicesRef.current && !servicesRef.current.contains(e.target as Node)) {
        setServicesOpen(false);
      }
      if (sectorsRef.current && !sectorsRef.current.contains(e.target as Node)) {
        setSectorsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    setServicesOpen(false);
    setSectorsOpen(false);
    setMobileOpen(false);
  }, [location.pathname]);

  const isActive = (path: string) => location.pathname === path;
  const isServiceActive = hizmetlerLinks.some(l => location.pathname.startsWith(l.href));
  const isSectorActive = sektorlerLinks.some(l => location.pathname.startsWith(l.href));

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled || !isHome
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/60'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <h1 className="flex items-center shrink-0">
            <Link
              to="/"
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className="flex items-center cursor-pointer"
            >
              <img
                src={logoUrl}
                alt="BIFF Digital - Growth & Campaign Agency"
                width="140"
                height="36"
                className="h-8 md:h-10 w-auto"
              />
            </Link>
          </h1>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-1">
            {/* Hizmetler Dropdown */}
            <div ref={servicesRef} className="relative">
              <button
                onClick={() => { setServicesOpen(!servicesOpen); setSectorsOpen(false); }}
                className={`flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  isServiceActive ? 'text-foreground-50' : 'text-foreground-300 hover:text-foreground-50'
                }`}
              >
                Hizmetler
                <i className={`ri-arrow-down-s-line text-xs transition-transform duration-200 ${servicesOpen ? 'rotate-180' : ''}`} />
              </button>
              {servicesOpen && (
                <div className="absolute top-full left-0 mt-1 w-72 bg-background-50/98 backdrop-blur-xl border border-background-200/60 rounded-xl p-2 shadow-lg">
                  {hizmetlerLinks.map((link) => (
                    <Link
                      key={link.href}
                      to={link.href}
                      className={`block px-4 py-3 rounded-lg text-sm transition-colors whitespace-nowrap ${
                        isActive(link.href)
                          ? 'bg-primary-500/10 text-primary-500 font-semibold'
                          : 'text-foreground-300 hover:text-foreground-50 hover:bg-background-100'
                      }`}
                    >
                      {link.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* Sektörler Dropdown */}
            <div ref={sectorsRef} className="relative">
              <button
                onClick={() => { setSectorsOpen(!sectorsOpen); setServicesOpen(false); }}
                className={`flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  isSectorActive ? 'text-foreground-50' : 'text-foreground-300 hover:text-foreground-50'
                }`}
              >
                Sektörler
                <i className={`ri-arrow-down-s-line text-xs transition-transform duration-200 ${sectorsOpen ? 'rotate-180' : ''}`} />
              </button>
              {sectorsOpen && (
                <div className="absolute top-full left-0 mt-1 w-56 bg-background-50/98 backdrop-blur-xl border border-background-200/60 rounded-xl p-2 shadow-lg">
                  {sektorlerLinks.map((link) => (
                    <Link
                      key={link.href}
                      to={link.href}
                      className={`block px-4 py-3 rounded-lg text-sm transition-colors whitespace-nowrap ${
                        isActive(link.href)
                          ? 'bg-primary-500/10 text-primary-500 font-semibold'
                          : 'text-foreground-300 hover:text-foreground-50 hover:bg-background-100'
                      }`}
                    >
                      {link.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* Ana Linkler */}
            {anaLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                  isActive(link.href) ? 'text-foreground-50' : 'text-foreground-300 hover:text-foreground-50'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* CTA + Mobile toggle */}
          <div className="flex items-center gap-4 shrink-0">
            <Link
              to="/iletisim"
              className="hidden md:inline-flex btn-primary text-sm whitespace-nowrap"
            >
              Görüşme Planla
              <i className="ri-arrow-right-line" />
            </Link>
            <button
              className="md:hidden text-foreground-50 p-2"
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label="Menüyü Aç/Kapat"
            >
              <i className={`ri-${mobileOpen ? 'close' : 'menu'}-line text-2xl`} />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="md:hidden bg-background-50/98 backdrop-blur-lg border-t border-background-200/60 max-h-[80vh] overflow-y-auto">
          <div className="px-4 py-4 space-y-1">

            {/* Hizmetler Accordion */}
            <button
              onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
              className="w-full flex items-center justify-between py-3 text-base font-medium text-foreground-200 hover:text-foreground-50 transition-colors cursor-pointer"
            >
              Hizmetler
              <i className={`ri-arrow-down-s-line transition-transform duration-200 ${mobileServicesOpen ? 'rotate-180' : ''}`} />
            </button>
            {mobileServicesOpen && (
              <div className="pl-4 pb-2 space-y-1">
                {hizmetlerLinks.map((link) => (
                  <Link
                    key={link.href}
                    to={link.href}
                    onClick={() => setMobileOpen(false)}
                    className={`block py-2 text-sm ${
                      isActive(link.href) ? 'text-primary-500 font-semibold' : 'text-foreground-400 hover:text-foreground-200'
                    }`}
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
            )}

            {/* Sektörler Accordion */}
            <button
              onClick={() => setMobileSectorsOpen(!mobileSectorsOpen)}
              className="w-full flex items-center justify-between py-3 text-base font-medium text-foreground-200 hover:text-foreground-50 transition-colors cursor-pointer"
            >
              Sektörler
              <i className={`ri-arrow-down-s-line transition-transform duration-200 ${mobileSectorsOpen ? 'rotate-180' : ''}`} />
            </button>
            {mobileSectorsOpen && (
              <div className="pl-4 pb-2 space-y-1">
                {sektorlerLinks.map((link) => (
                  <Link
                    key={link.href}
                    to={link.href}
                    onClick={() => setMobileOpen(false)}
                    className={`block py-2 text-sm ${
                      isActive(link.href) ? 'text-primary-500 font-semibold' : 'text-foreground-400 hover:text-foreground-200'
                    }`}
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
            )}

            {/* Ana Linkler */}
            {anaLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                onClick={() => setMobileOpen(false)}
                className={`block py-3 text-base font-medium ${
                  isActive(link.href) ? 'text-foreground-50' : 'text-foreground-300 hover:text-foreground-50'
                }`}
              >
                {link.label}
              </Link>
            ))}

            <Link
              to="/iletisim"
              onClick={() => setMobileOpen(false)}
              className="btn-primary w-full justify-center mt-4 mb-2 whitespace-nowrap"
            >
              Görüşme Planla
              <i className="ri-arrow-right-line" />
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
}